import { CommonModule } from '@angular/common';
import { Component, Input, NgModule, OnDestroy, OnInit, Pipe, PipeTransform } from '@angular/core';
import { AbstractControl, ControlContainer, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';
import { Subject } from 'rxjs';
import { debounceTime, filter, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { User, UserService } from '../services/user';

@Component({
  selector: 'to-user-search',
  templateUrl: './user-search.html',
  styleUrls: ['./user-search.scss'],
})
export class UserSearchComponent implements OnInit, OnDestroy {

  private _initialValue: User;

  userNameControl: FormControl;
  private _userIdControl: AbstractControl;

  loading = false;
  filteredUsers: User[];

  private _lastSearch: string;
  private _destroy$ = new Subject<void>();

  @Input() isRequired = true;
  @Input() placeholder: string;
  @Input() controlName: string;
  @Input() private control: FormControl;
  @Input() allowDelete = true;
  @Input() userNameField = 'Title';
  @Input() enable = () => true;
  @Input()
  set prefill(value: User) {
    this._initialValue = value;
    if (this.userNameControl) {
      this.userNameControl.setValue(this._initialValue);
    }
  }

  constructor(
    private user: UserService,
    private controlContainer: ControlContainer,
  ) { }

  ngOnInit() {
    if (this.control) {
      this._userIdControl = this.control;
    } else {
      this._userIdControl = this.controlContainer.control.get(this.controlName);
    }
    this.userNameControl = new FormControl(this._userIdControl.value, () => this._userIdControl.errors);

    if (this._initialValue) {
      this.userNameControl.setValue(this._initialValue);
    }

    this._userIdControl.valueChanges
      .pipe(takeUntil(this._destroy$), startWith(this._userIdControl.value))
      .subscribe(() => {
        this.userNameControl[this._userIdControl.valid ? 'disable' : this.enable() ? 'enable' : 'disable']();
      });


    this.userNameControl.valueChanges.pipe(
      debounceTime(250),
      filter(searchFor => typeof searchFor === 'string' && this._lastSearch !== searchFor),
      tap(searchFor => this._lastSearch = searchFor),
      tap(() => this.loading = true),
      switchMap(searchFor => this.user.search(searchFor)),
      tap(() => this.loading = false),
      takeUntil(this._destroy$)
    ).subscribe(users => this.filteredUsers = users);
  }

  ngOnDestroy() {
    if (this._destroy$) {
      this._destroy$.next();
      this._destroy$.unsubscribe();
    }
  }

  displayFn() {
    const self = this;
    return user => {
      if (user) {
        let value = user[self.userNameField] || user.Title;
        if (user.UserPrincipalName) {
          value += ` (${user.UserPrincipalName.replace('@corpaa.aa.com', '')})`;
        }
        return value;
      }
    };
  }

  selectUser(user?: User) {
    this._lastSearch = user[this.userNameField];
    this._userIdControl.setValue(user.Id);
    this._userIdControl.markAsDirty();
    this.userNameControl.disable();
  }

  clearUser() {
    this._lastSearch = null;
    this._userIdControl.setValue(null);
    this.userNameControl.setValue(null);
    this._userIdControl.markAsDirty();
    this.userNameControl.enable();
  }

}

@Pipe({
  name: 'userIdFromName'
})
export class UserIdFromNamePipe implements PipeTransform {

  transform(value: string): string {
    return (value || '').substr(0, (value || '').indexOf('@'));
  }

}

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    FontAwesomeModule,
  ],
  declarations: [
    UserSearchComponent,
    UserIdFromNamePipe,
  ],
  exports: [
    UserSearchComponent,
    UserIdFromNamePipe,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    UserIdFromNamePipe,
    MatProgressSpinnerModule,
    FontAwesomeModule,
  ],
})
export class UserSearchModule {
  constructor(
    library: FaIconLibrary
  ) {
    library.addIcons(faTimes);
  }
}
